import { useEffect, useState } from "react";
import { arrowDownIcon, rightArrowIcon } from "../../assets/icons/SvgIconList";
import { FormRightModalWrapper, ICButton, ICImage, Loader } from "../../core-component/ui";
import { IPagination } from "../../data/AppInterface";
import UseEventCallbackHook from "../../hooks/UseEventCallbackHook";
import UsePaginationHook from "../../hooks/UsePaginationHook";
import UseTranslationHook from "../../hooks/UseTranslationHook";
import {
  getInitials,
  limitString,
  prepareMessageFromParams,
  stringToColor,
} from "../../utils/AppFunctions";
import { formatPurchaseDate, formatPurchaseTime } from "../../utils/PurchaseFormat";
import {
  fetchVersionHistory,
  IVersionChange,
  IVersionHistoryItem,
} from "./BusinessInformationController";

interface IChangeLineProps {
  change: IVersionChange;
}

interface IBusinessInformationHistoryProps {
  handleClose: () => void;
}

const VISIBLE_CHANGES = 3;
const VALUE_PREVIEW_LENGTH = 24;

const ChangeLine: React.FC<IChangeLineProps> = ({ change }) => (
  <div className="py-1">
    <div className="font-medium">{change.field_name}</div>
    <div className="flex items-center text-muted" title={`${change.old_value ?? "-"} → ${change.new_value ?? "-"}`}>
      <span>{limitString(change.old_value ?? "-", VALUE_PREVIEW_LENGTH)}</span>
      <span className="mx-1 size-5 shrink-0 text-default">{rightArrowIcon}</span>
      <span>{limitString(change.new_value ?? "-", VALUE_PREVIEW_LENGTH)}</span>
    </div>
  </div>
);

const BusinessInformationHistory: React.FC<IBusinessInformationHistoryProps> = ({
  handleClose,
}) => {
  const { t } = UseTranslationHook();
  const { pagination, setChangePagination } = UsePaginationHook();
  const [items, setItems] = useState<IVersionHistoryItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [expanded, setExpanded] = useState<number[]>([]);

  const loadPage = UseEventCallbackHook(async (page: IPagination) => {
    const result = await fetchVersionHistory(page);
    if (result) {
      setItems((previous) =>
        page.currentPage === 1 ? result.items : [...previous, ...result.items],
      );
      setChangePagination(result.pagination);
    } else if (page.currentPage === 1) {
      handleClose();
    }
  });

  const loadFirstPage = UseEventCallbackHook(() =>
    loadPage({ ...pagination, currentPage: 1 }).finally(() => setLoading(false)),
  );

  useEffect(() => {
    loadFirstPage();
  }, [loadFirstPage]);

  const handleMore = () => {
    setLoadingMore(true);
    loadPage({ ...pagination, currentPage: pagination.currentPage + 1 }).finally(() =>
      setLoadingMore(false),
    );
  };

  const entry = (item: IVersionHistoryItem) => {
    const isExpanded = expanded.includes(item.version_no);
    const isFirstVersion = item.version_no === 1;
    const changes = isExpanded ? item.log : item.log.slice(0, VISIBLE_CHANGES);
    const name = item.action_by ?? "";

    return (
      <div key={item.version_no} className="mb-2">
        <div className="flex">
          <div className="size-11 rounded-full overflow-hidden shrink-0">
            {item.profile_media_url ? (
              <ICImage
                className="h-full w-full object-cover rounded-full"
                imageUrl={item.profile_media_url}
                isThumbnail
              />
            ) : (
              <div
                className="h-full w-full grid place-items-center text-on-accent font-bold"
                style={{ backgroundColor: stringToColor(name) }}
              >
                {getInitials(name)}
              </div>
            )}
          </div>
          <div className="ml-3">
            <div className="text-sm text-muted">
              {prepareMessageFromParams(t("action_date_at_time"), [
                ["actionDate", formatPurchaseDate(item.action_date)],
                ["actionTime", formatPurchaseTime(item.action_date)],
              ])}
            </div>
            <div>{item.title}</div>
          </div>
        </div>
        <div className="ml-5 my-1 pl-5 py-2 border-l-2 border-muted text-sm">
          {isFirstVersion ? (
            <div className="text-muted">{t("business_info_initial_version")}</div>
          ) : (
            <>
              {changes.map((change) => (
                <ChangeLine key={change.field_name} change={change} />
              ))}
              {item.log.length > VISIBLE_CHANGES && (
                <ICButton
                  type="button"
                  className="w-fit p-0 pt-1 bg-transparent text-accent hover:underline"
                  onClick={() =>
                    setExpanded((previous) =>
                      isExpanded
                        ? previous.filter((versionNo) => versionNo !== item.version_no)
                        : [...previous, item.version_no],
                    )
                  }
                >
                  {isExpanded ? t("read_less") : t("read_more")}
                </ICButton>
              )}
            </>
          )}
        </div>
      </div>
    );
  };

  return (
    <FormRightModalWrapper
      containerClassName="!w-128"
      onClose={handleClose}
      title={t("change_history")}
      mutedTitle={t("business_information")}
    >
      {loading ? (
        <Loader />
      ) : items.length ? (
        <>
          {items.map(entry)}
          {pagination.currentPage < pagination.totalPages && (
            <div className="flex justify-center">
              <ICButton
                type="button"
                className="size-10 mt-2 p-0 grid place-items-center rounded-full"
                onClick={handleMore}
                loading={loadingMore}
              >
                <span className="size-5">{arrowDownIcon}</span>
              </ICButton>
            </div>
          )}
        </>
      ) : (
        <div className="flex h-full items-center justify-center text-muted">
          {t("no_history_found")}
        </div>
      )}
    </FormRightModalWrapper>
  );
};

export default BusinessInformationHistory;
